// Navigation model — ONE description of where an operator can go (task 355.4).
//
// Consumed by the sidebar (rendered as grouped AntD Menu items) and by the ⌘K command palette
// (flattened destinations), so the two can never disagree about a route or a permission gate.
// Gates are the ones the pre-355.4 Sidebar applied: hasRole('SYSTEM_ADMIN'), TENANT_ADMIN,
// hasPermission('IE_ADMIN_VIEW'), hasPermission('SYSTEM_CONFIG').
// Task 381: Documents is presented as DATA HUB (label only — `/documents/...` URLs are unchanged); Schema
//   Studio and Workflows sit under Configure › Designers; "Integration hub" is Integration engine, under
//   Operate rather than a one-item group; Chart of accounts left General ledger.
// Task 382: Configure › Chart of accounts is the editor — SYSTEM_ADMIN only, because the chart is shared by
//   every tenant.
// Task 372: the chart of accounts is NOT in this menu. It was (task 359), mirrored section by section,
//   which put the same tree in two places and made the sidebar as deep as the chart. It lives on the
//   Accounts page, beside the list it filters — one tree, where the accounts are.
// Task 401.11: DATA HUB is the caller's own tree (api 401.5 drops types without VIEW and empty categories);
//   a document type with no operator create path (isSystemIntake) is marked in the sidebar and matches
//   "system intake" in the palette.

import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  Braces,
  FileInput,
  Cable,
  ChartColumn,
  CircleCheck,
  Coins,
  Database,
  DraftingCompass,
  FileText,
  Folder,
  KeyRound,
  Landmark,
  Scale,
  Layers,
  LayoutDashboard,
  ListTree,
  Settings,
  ShieldCheck,
  User,
  Users,
  UsersRound,
  Workflow,
  Wrench,
} from 'lucide-react';
import type { DocumentTypeTreeResponse } from '../../../api/generated/schema';

export const DOC_CAT_PREFIX = 'doc-cat-';

/** Palette keyword (and sidebar hint) for document types with no operator create path. */
export const SYSTEM_INTAKE_KEYWORD = 'system intake';

/** Sub-menu opener for the integration pages (not a route). */
export const INTEGRATION_KEY = 'integration-engine';

/** Sub-menu opener for the schema and workflow designers (not a route). */
export const DESIGNERS_KEY = 'designers';

/** Sub-menu opener for the meta-document types — shown as DATA HUB, keyed by its old route. */
export const DATA_HUB_KEY = '/documents';

/** Menu key of a document category (a non-navigable opener). */
export const docCatKey = (catCode: string): string => `${DOC_CAT_PREFIX}${catCode}`;

export interface NavNode {
  /** A route for leaves; an opener key for parents. */
  key: string;
  label: string;
  icon?: LucideIcon;
  /** Numeric badge (active documents, pending tasks). */
  count?: number;
  /** A document type whose documents arrive from the App, integrations or workflows — no New (401.11). */
  systemIntake?: boolean;
  /** Extra words the command palette matches on. */
  keywords?: string[];
  children?: NavNode[];
}

export interface NavGroup {
  key: string;
  label: string;
  items: NavNode[];
}

export interface NavContext {
  hasRole: (code: string) => boolean;
  hasPermission: (code: string) => boolean;
  documentTree?: DocumentTypeTreeResponse;
  /** Chart of accounts; without it Accounts is a single entry. */
  pendingTaskCount: number;
}

/**
 * The document category that holds the payment types — found by the list view the api assigns
 * (`listViewType: PAYMENT_PROJECTION`), never by a hard-coded category code (seeds use both PAYMENT
 * and PAYMENTS). The category with the most payment types wins.
 */
export function paymentsCategoryCode(tree: DocumentTypeTreeResponse | undefined): string | null {
  let best: { code: string; n: number } | null = null;
  for (const cat of tree?.categories ?? []) {
    const n = cat.documentTypes.filter(
      (dt) => dt.isActive && dt.listViewType === 'PAYMENT_PROJECTION',
    ).length;
    if (n > 0 && (!best || n > best.n)) best = { code: cat.categoryCode, n };
  }
  return best?.code ?? null;
}

/**
 * Where the Payments shortcut points and which document types it covers (task 381).
 *
 * A payment document lives at `/documents/<type>/...`, the same shape as every other document, so the URL
 * alone cannot say it belongs to Payments. The type codes can: these are the types the shortcut aggregates.
 */
export interface PaymentsShortcut {
  path: string;
  typeCodes: string[];
}

export function paymentsShortcut(
  tree: DocumentTypeTreeResponse | undefined,
): PaymentsShortcut | null {
  const code = paymentsCategoryCode(tree);
  if (!code) return null;
  const category = tree?.categories.find((c) => c.categoryCode === code);
  return {
    path: `/documents/category/${code}`,
    typeCodes: (category?.documentTypes ?? [])
      .filter((dt) => dt.isActive && dt.listViewType === 'PAYMENT_PROJECTION')
      .map((dt) => dt.typeCode),
  };
}

/** True when a location is the Payments shortcut or a payment document under it. */
function isUnderPayments(pathname: string, payments: PaymentsShortcut | null | undefined): boolean {
  if (!payments) return false;
  if (pathname.startsWith('/documents/category/')) return true;
  const typeCode = pathname.split('/')[2];
  return pathname.startsWith('/documents/') && payments.typeCodes.includes(typeCode);
}

function documentNodes(tree: DocumentTypeTreeResponse | undefined): NavNode[] {
  if (!tree?.categories.length) return [];
  return tree.categories
    .map((cat) => ({
      key: docCatKey(cat.categoryCode),
      label: cat.categoryName,
      icon: Folder,
      children: cat.documentTypes
        .filter((dt) => dt.isActive)
        .map(
          (dt): NavNode => ({
            key: `/documents/${dt.typeCode}`,
            label: dt.typeName,
            count: dt.activeDocumentCount || undefined,
            keywords: dt.isSystemIntake
              ? [dt.typeCode, cat.categoryName, SYSTEM_INTAKE_KEYWORD]
              : [dt.typeCode, cat.categoryName],
            ...(dt.isSystemIntake ? { systemIntake: true } : {}),
          }),
        ),
    }))
    .filter((cat) => cat.children.length > 0);
}

export function buildNavGroups(ctx: NavContext): NavGroup[] {
  const { hasRole, hasPermission } = ctx;
  const isSystemAdmin = hasRole('SYSTEM_ADMIN');
  const isAdmin = isSystemAdmin || hasRole('TENANT_ADMIN');
  const payments = paymentsShortcut(ctx.documentTree);

  const operate: NavNode[] = [
    { key: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    {
      key: '/assignments',
      label: 'My assignments',
      icon: CircleCheck,
      count: ctx.pendingTaskCount || undefined,
      keywords: ['approvals', 'assignments', 'access', 'restricted', 'masked'],
    },
    ...(payments
      ? [
          {
            key: payments.path,
            label: 'Payments',
            icon: Layers,
            keywords: ['transfers', 'all payments'],
          },
        ]
      : []),
    {
      key: DATA_HUB_KEY,
      label: 'Data hub',
      icon: FileText,
      keywords: ['documents'],
      children: documentNodes(ctx.documentTree),
    },
    { key: '/customers', label: 'Customers', icon: Users, keywords: ['clients', 'kyc'] },
  ];

  const ledger: NavNode[] = [
    {
      key: '/gl',
      label: 'General ledger',
      icon: Landmark,
      children: [
        { key: '/gl/accounts', label: 'Accounts', keywords: ['chart of accounts', 'coa'] },
        { key: '/gl/ledger', label: 'Ledger', keywords: ['journals', 'transactions'] },
        { key: '/gl/balances', label: 'Balances' },
        {
          key: '/gl/transaction-types',
          label: 'Transaction types',
          keywords: ['posting', 'forms', 'debit credit', 'type codes'],
        },
      ],
    },
    {
      key: '/reports',
      label: 'Reports',
      icon: ChartColumn,
      children: [
        { key: '/reports/balance-sheet', label: 'Balance sheet' },
        { key: '/reports/profit-loss', label: 'Profit & loss', keywords: ['p&l', 'income'] },
        {
          key: '/reports/trial-balance',
          label: 'Trial balance',
          keywords: ['gl movements', 'turnover', 'chart of accounts'],
        },
        {
          key: '/reports/trial-balance-accounts',
          label: 'Trial balance by account',
          keywords: ['accounts', 'balances', 'paged', 'export', 'csv', 'wallets'],
        },
        {
          key: '/reports/holds',
          label: 'Active holds',
          keywords: ['holds', 'reserved', 'available', 'memo'],
        },
        { key: '/reports/safeguarding', label: 'Safeguarding', keywords: ['client money'] },
        { key: '/reports/capital-requirements', label: 'Capital requirements' },
      ],
    },
    { key: '/fees/plans', label: 'Fees', icon: Coins, keywords: ['pricing', 'plans'] },
    {
      key: '/tools',
      label: 'Tools',
      icon: Wrench,
      children: [
        { key: '/tools/balance-calculation', label: 'Balance calculation' },
        { key: '/tools/fx-rates', label: 'FX rates', keywords: ['exchange'] },
      ],
    },
  ];

  // Task 531 (ADR-078): the firm's open FX position and its hedge orders — for whoever manages FX orders.
  if (hasPermission('FX_ORDER_MANAGE')) {
    ledger.splice(2, 0, {
      key: '/treasury',
      label: 'Treasury',
      icon: Scale,
      children: [
        {
          key: '/treasury/fx-position',
          label: 'FX position',
          keywords: ['hedge', 'open position', 'transit', '10815', 'fx settlement'],
        },
      ],
    });
  }

  if (hasPermission('IE_ADMIN_VIEW')) {
    operate.push({
      // Opener key must differ from its first child's route (AntD rejects duplicate keys; the
      // pre-355.4 menu used '/ops/integration' for both).
      key: INTEGRATION_KEY,
      label: 'Integration engine',
      icon: Cable,
      keywords: ['integration hub', 'ie'],
      children: [
        { key: '/ops/integration', label: 'Dashboard' },
        { key: '/ops/integration/dlq', label: 'Dead-letter queue', keywords: ['dlq'] },
        { key: '/ops/integration/connectors', label: 'Connectors' },
        { key: '/ops/integration/clearbank', label: 'ClearBank settings' },
        { key: '/ops/integration/requests', label: 'Request audit' },
        { key: '/ops/integration/webhooks', label: 'Webhooks' },
        { key: '/ops/integration/reconciliation', label: 'Reconciliation' },
        { key: '/ops/integration/tenant-flags', label: 'Tenant flags' },
      ],
    });
  }

  const designers: NavNode[] = [];
  if (isSystemAdmin) {
    designers.push({ key: '/admin/schema-studio', label: 'Schema Studio', icon: Braces });
    designers.push({
      key: '/admin/import-profiles',
      label: 'Import profiles',
      icon: FileInput,
      keywords: ['batch import', 'settlement file', 'mapping'],
    });
  }
  designers.push({
    key: '/workflows',
    label: 'Workflows',
    icon: Workflow,
    keywords: ['workflow studio'],
  });

  const configure: NavNode[] = [
    { key: DESIGNERS_KEY, label: 'Designers', icon: DraftingCompass, children: designers },
    { key: '/reference', label: 'Reference data', icon: Database },
  ];
  if (isSystemAdmin) {
    configure.push({
      key: '/admin/chart-of-accounts',
      label: 'Chart of accounts',
      icon: ListTree,
      keywords: ['coa', 'account codes', 'gl codes'],
    });
    configure.push({ key: '/admin/tenants', label: 'Tenants', icon: Building2 });
  }
  if (isAdmin) {
    configure.push({ key: '/admin/users', label: 'Users', icon: User });
    configure.push({ key: '/admin/groups', label: 'Groups', icon: UsersRound });
    configure.push({ key: '/admin/roles', label: 'Roles', icon: ShieldCheck });
    // 401.14: who can see, create and act on each document type, and why.
    configure.push({
      key: '/admin/access',
      label: 'Access',
      icon: KeyRound,
      keywords: ['permissions', 'bindings', 'channels', 'who can'],
    });
  }
  if (hasPermission('SYSTEM_CONFIG')) {
    configure.push({ key: '/settings/tenant', label: 'Settings', icon: Settings });
    // 389: which chart-of-accounts nodes each report line counts (defaults + tenant overrides).
    configure.push({
      key: '/settings/report-mappings',
      label: 'Report mappings',
      icon: ChartColumn,
      keywords: ['report lines', 'balance sheet', 'safeguarding', 'capital'],
    });
    // 438.2: the tenant's labels and "may go negative" rule per platform account code.
    configure.push({
      key: '/settings/account-codes',
      label: 'Account codes',
      icon: ListTree,
      keywords: ['chart of accounts', 'labels', 'negative balance', 'overdraft'],
    });
    // 439: per card product, the payable / revenue / expense accounts the netting import routes to.
    configure.push({
      key: '/settings/card-product-routing',
      label: 'Card product routing',
      icon: ListTree,
      keywords: [
        'card products',
        'netting',
        'processor payable',
        'settlement accounts',
        'visa',
        'mastercard',
      ],
    });
  }

  const groups: NavGroup[] = [
    { key: 'grp-operate', label: 'Operate', items: operate },
    { key: 'grp-ledger', label: 'Ledger', items: ledger },
    { key: 'grp-configure', label: 'Configure', items: configure },
  ];

  return groups;
}

export interface NavDestination {
  path: string;
  /** "General ledger › Ledger" */
  label: string;
  group: string;
  icon?: LucideIcon;
  keywords: string[];
}

/** Every navigable leaf, with its breadcrumb label — for the command palette. */
export function flattenDestinations(groups: NavGroup[]): NavDestination[] {
  const out: NavDestination[] = [];
  const seen = new Set<string>();

  const walk = (nodes: NavNode[], trail: string[], group: string, icon?: LucideIcon): void => {
    for (const node of nodes) {
      const nodeIcon = node.icon ?? icon;
      if (node.children) {
        walk(node.children, [...trail, node.label], group, nodeIcon);
      } else if (node.key.startsWith('/') && !seen.has(node.key)) {
        seen.add(node.key);
        out.push({
          path: node.key,
          label: [...trail, node.label].join(' › '),
          group,
          icon: nodeIcon,
          keywords: node.keywords ?? [],
        });
      }
    }
  };

  for (const g of groups) walk(g.items, [], g.label);
  return out;
}

/**
 * Sidebar key to highlight for a location. `payments` (from `paymentsShortcut`) lets a payment document
 * highlight the Payments shortcut rather than nothing (task 381).
 */
export function deriveSelectedKey(pathname: string, payments?: PaymentsShortcut | null): string {
  if (payments && isUnderPayments(pathname, payments)) {
    return pathname.startsWith('/documents/category/') ? pathname.split('?')[0] : payments.path;
  }
  const exactPrefixes = ['/documents/', '/gl/', '/tools/', '/reports/', '/ops/integration'];
  if (exactPrefixes.some((p) => pathname.startsWith(p))) return pathname.split('?')[0];
  const prefixes = [
    '/dashboard',
    '/customers',
    '/fees/plans',
    '/workflows',
    '/assignments',
    '/reference',
    '/admin/schema-studio',
    '/admin/chart-of-accounts',
    '/admin/tenants',
    '/admin/users',
    '/admin/groups',
    '/admin/roles',
    '/admin/access',
    // Exact settings pages: a bare '/settings' prefix selected no item at all.
    '/settings/tenant',
    '/settings/report-mappings',
  ];
  return prefixes.find((p) => pathname.startsWith(p)) ?? '/dashboard';
}

/**
 * Sub-menus that should start open for a location.
 *
 * The Payments shortcut and payment documents are outside DATA HUB, so they do not open it (task 381) —
 * `/documents/category/*` shares the `/documents/` prefix only because of how it is routed.
 */
export function deriveOpenKeys(pathname: string, payments?: PaymentsShortcut | null): string[] {
  const keys: string[] = [];
  const underPayments =
    pathname.startsWith('/documents/category/') || isUnderPayments(pathname, payments);
  if (pathname.startsWith('/documents/') && !underPayments) keys.push(DATA_HUB_KEY);
  if (pathname.startsWith('/gl/')) keys.push('/gl');
  if (pathname.startsWith('/tools/')) keys.push('/tools');
  if (pathname.startsWith('/reports/')) keys.push('/reports');
  if (pathname.startsWith('/ops/integration')) keys.push(INTEGRATION_KEY);
  if (pathname.startsWith('/workflows') || pathname.startsWith('/admin/schema-studio')) {
    keys.push(DESIGNERS_KEY);
  }
  return keys;
}

/** Pages shown before anything is typed. */
const DEFAULT_PAGES = [
  '/dashboard',
  '/assignments',
  '/customers',
  '/gl/ledger',
  '/reports/balance-sheet',
  '/workflows',
];

const MAX_PAGES = 8;

/** Command-palette matching: every typed term must appear in the label, group or keywords. */
export function matchDestinations(destinations: NavDestination[], query: string): NavDestination[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return DEFAULT_PAGES.map((p) => destinations.find((d) => d.path === p)).filter(
      (d): d is NavDestination => Boolean(d),
    );
  }
  const terms = q.split(/\s+/);
  return destinations
    .filter((d) => {
      const haystack = [d.label, d.group, ...d.keywords].join(' ').toLowerCase();
      return terms.every((t) => haystack.includes(t));
    })
    .slice(0, MAX_PAGES);
}
